import { z } from 'zod';
import { IsoDateSchema, LocaleSchema, UuidSchema } from './common';

export const CONTACT_STATUSES = ['active', 'archived', 'merged', 'deleted'] as const;
export type ContactStatus = (typeof CONTACT_STATUSES)[number];

export const CONSENT_STATES = ['granted', 'denied', 'unknown'] as const;
export type ConsentState = (typeof CONSENT_STATES)[number];

export const CONSENT_CHANNELS = ['email', 'sms'] as const;
export type ConsentChannel = (typeof CONSENT_CHANNELS)[number];

export const CONSENT_PURPOSES = ['marketing', 'review_request', 'transactional'] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

export const LAWFUL_BASES = [
  'consent',
  'existing_customer',
  'legitimate_interest',
  'contract',
  'unknown',
] as const;
export type LawfulBasis = (typeof LAWFUL_BASES)[number];

export const CONSENT_EVENT_SOURCES = [
  'manual',
  'import',
  'unsubscribe_link',
  'sms_keyword',
  'public_page',
  'api',
  'provider_complaint',
  'admin',
] as const;
export type ConsentEventSource = (typeof CONSENT_EVENT_SOURCES)[number];

/** How the business collected a consent (stored in `consent_events.evidence.method`). */
export const CONSENT_METHODS = [
  'in_person_verbal',
  'in_person_written',
  'online_form',
  'phone_call',
  'email_reply',
  'other',
] as const;
export type ConsentMethod = (typeof CONSENT_METHODS)[number];

export const CONTACT_SOURCE_TYPES = [
  'manual',
  'import',
  'api',
  'integration',
  'public_form',
  /**
   * Created by an admin from a forms share-link answer (`id` = form id). Deliberately NOT in the
   * reviews auto-send sources: such a contact never gets an automatic review request.
   */
  'form_share',
  /** Walk-in added at an event check-in (v1.1); outside the reviews auto-send sources too. */
  'form_walk_in',
] as const;
export type ContactSourceType = (typeof CONTACT_SOURCE_TYPES)[number];

export const ContactSourceSchema = z.object({
  type: z.enum(CONTACT_SOURCE_TYPES),
  id: z.string().optional(),
  label: z.string().optional(),
});
export type ContactSource = z.infer<typeof ContactSourceSchema>;

export const SUPPRESSION_REASONS = [
  'unsubscribed',
  'bounced',
  'complaint',
  'manual',
  'invalid',
  'sms_stop',
] as const;
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number];

/** Suppressions that record the person's own opt-out: never lifted by an import or a staff member. */
export const PERMANENT_SUPPRESSION_REASONS: readonly SuppressionReason[] = [
  'unsubscribed',
  'complaint',
  'sms_stop',
];

export const IMPORT_STATUSES = [
  'uploaded',
  'parsing',
  'mapping',
  'ready',
  'committing',
  'completed',
  'failed',
  'canceled',
  'rolled_back',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

export const IMPORT_ROW_STATUSES = [
  'pending',
  'valid',
  'invalid',
  'duplicate',
  'created',
  'updated',
  'skipped',
  'rejected',
] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

export const IMPORT_MODES = ['basic', 'advanced'] as const;
export type ImportMode = (typeof IMPORT_MODES)[number];

export const DATA_QUALITY_ISSUE_TYPES = [
  'possible_duplicate',
  'invalid_email',
  'invalid_phone',
  'missing_channel',
  'sensitive_data',
  'unmatched_event',
  'malformed_date',
] as const;
export type DataQualityIssueType = (typeof DATA_QUALITY_ISSUE_TYPES)[number];

export const DATA_QUALITY_STATUSES = ['open', 'resolved', 'dismissed'] as const;

/** Optional string from a form: '' and whitespace-only become undefined. */
const optionalText = (max: number) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().trim().max(max).optional(),
  );
/** Nullable string for PATCH: '' becomes null (clear the field). */
const clearableText = (max: number) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
    z.string().trim().max(max).nullable().optional(),
  );
const emailInput = z.preprocess(
  (v) => (typeof v === 'string' ? (v.trim() === '' ? undefined : v.trim()) : v),
  z.email().max(254).optional(),
);
const clearableEmail = z.preprocess(
  (v) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : v),
  z.email().max(254).nullable().optional(),
);
const clearableDate = z.preprocess(
  (v) => (v === '' ? null : v),
  IsoDateSchema.nullable().optional(),
);

export const TagSchema = z.string().trim().min(1).max(40);
export const TagListSchema = z.array(TagSchema).max(50);

export const ContactSchema = z.object({
  id: UuidSchema,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phoneE164: z.string().nullable(),
  phoneRaw: z.string().nullable(),
  country: z.string().nullable(),
  locale: LocaleSchema.nullable(),
  birthDate: IsoDateSchema.nullable(),
  attributes: z.record(z.string(), z.unknown()),
  tags: z.array(z.string()),
  status: z.enum(CONTACT_STATUSES),
  consentEmail: z.enum(CONSENT_STATES),
  consentSms: z.enum(CONSENT_STATES),
  lawfulBasis: z.enum(LAWFUL_BASES).optional(),
  source: ContactSourceSchema.optional(),
  importId: UuidSchema.nullable().optional(),
  mergedIntoId: UuidSchema.nullable().optional(),
  lastActivityAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Contact = z.infer<typeof ContactSchema>;

/** Small identity card used in duplicate lists, merge previews and pickers. */
export const ContactBriefSchema = z.object({
  id: UuidSchema,
  name: z.string().nullable(),
  email: z.string().nullable(),
  phoneE164: z.string().nullable(),
  status: z.enum(CONTACT_STATUSES),
  createdAt: z.string(),
});
export type ContactBrief = z.infer<typeof ContactBriefSchema>;

export const CreateContactRequestSchema = z
  .object({
    /** Single "name" box (quick add): split into first/last when those are not given. */
    name: optionalText(200),
    firstName: optionalText(100),
    lastName: optionalText(100),
    email: emailInput,
    phone: optionalText(32),
    birthDate: z.preprocess((v) => (v === '' ? undefined : v), IsoDateSchema.optional()),
    locale: LocaleSchema.optional(),
    tags: TagListSchema.default([]),
    consentEmail: z.enum(CONSENT_STATES).default('unknown'),
    consentSms: z.enum(CONSENT_STATES).default('unknown'),
    /** How the consent was collected (evidence on the consent event). */
    consentMethod: z.enum(CONSENT_METHODS).optional(),
    consentNote: optionalText(500),
    lawfulBasis: z.enum(LAWFUL_BASES).default('unknown'),
    attributes: z.record(z.string(), z.unknown()).default({}),
  })
  .refine((v) => Boolean(v.email || v.phone), {
    message: 'email or phone is required',
    path: ['email'],
  });
export type CreateContactRequest = z.input<typeof CreateContactRequestSchema>;

export const UpdateContactRequestSchema = z
  .object({
    firstName: clearableText(100),
    lastName: clearableText(100),
    displayName: clearableText(200),
    email: clearableEmail,
    phone: clearableText(32),
    birthDate: clearableDate,
    locale: LocaleSchema.nullable().optional(),
    tags: TagListSchema.optional(),
    /** Custom fields (Pro, `schema.custom`) keyed by field definition id. `null` clears a value. */
    attributes: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'nothing to update' });
export type UpdateContactRequest = z.input<typeof UpdateContactRequestSchema>;

export const CreateContactResponseSchema = z.object({
  contact: ContactSchema,
  /** Same phone / same name, not blocking (shared family phones are legitimate). */
  possibleDuplicates: z.array(ContactBriefSchema),
});
export type CreateContactResponse = z.infer<typeof CreateContactResponseSchema>;

// ── List / filters ───────────────────────────────────────────────────────────

export const CONTACT_SORTS = [
  'created_desc',
  'created_asc',
  'name_asc',
  'name_desc',
  'activity_desc',
] as const;
export type ContactSort = (typeof CONTACT_SORTS)[number];

export const CONTACT_LIST_STATUSES = ['active', 'archived', 'all'] as const;

const queryBool = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((v) => v === true || v === 'true' || v === '1');
const queryTags = z
  .union([TagSchema, z.array(TagSchema)])
  .transform((v) => (Array.isArray(v) ? v : [v]))
  .pipe(z.array(TagSchema).max(20));

/**
 * Custom-field filter (Pro, `schema.custom`): `<field definition uuid>:<op>:<value>`.
 * `in` takes `|`-separated values; `set`/`unset` ignore the value. Compiled by the schema module.
 */
export const CONTACT_ATTR_FILTER_OPS = [
  'eq',
  'neq',
  'in',
  'gte',
  'lte',
  'contains',
  'set',
  'unset',
] as const;
export const ContactAttrFilterSchema = z
  .string()
  .max(260)
  .regex(/^[0-9a-f-]{36}:(eq|neq|in|gte|lte|contains|set|unset):[\s\S]{0,200}$/);
const queryAttrs = z
  .union([ContactAttrFilterSchema, z.array(ContactAttrFilterSchema)])
  .transform((v) => (Array.isArray(v) ? v : [v]))
  .pipe(z.array(ContactAttrFilterSchema).max(10));

/** Filters shared by list, export and bulk actions ("all matching"). */
export const ContactFilterSchema = z.object({
  attr: queryAttrs.optional(),
  q: z.string().trim().max(200).optional(),
  tag: queryTags.optional(),
  consentEmail: z.enum(CONSENT_STATES).optional(),
  consentSms: z.enum(CONSENT_STATES).optional(),
  status: z.enum(CONTACT_LIST_STATUSES).default('active'),
  hasEmail: queryBool.optional(),
  hasPhone: queryBool.optional(),
  createdFrom: IsoDateSchema.optional(),
  createdTo: IsoDateSchema.optional(),
  importId: UuidSchema.optional(),
});
export type ContactFilter = z.infer<typeof ContactFilterSchema>;
export type ContactFilterInput = z.input<typeof ContactFilterSchema>;

export const ContactListQuerySchema = ContactFilterSchema.extend({
  sort: z.enum(CONTACT_SORTS).default('created_desc'),
  /**
   * Pro (`schema.custom`): sort by a SORTABLE custom contact field, `<field uuid>:asc|desc`.
   * Overrides `sort`; empty values always come last. Compiled by the schema module.
   */
  attrSort: z
    .string()
    .regex(/^[0-9a-f-]{36}:(asc|desc)$/)
    .optional(),
  cursor: z.string().max(600).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ContactListQuery = z.infer<typeof ContactListQuerySchema>;

export const ContactListResponseSchema = z.object({
  items: z.array(ContactSchema),
  nextCursor: z.string().nullable(),
  /** Total matching the filters (independent of the cursor). */
  total: z.number().int(),
});
export type ContactListResponse = z.infer<typeof ContactListResponseSchema>;

export const TagCountSchema = z.object({ tag: z.string(), count: z.number().int() });
export const TagListResponseSchema = z.object({ items: z.array(TagCountSchema) });

// ── Detail ───────────────────────────────────────────────────────────────────

export const PHONE_TYPES = ['mobile', 'landline', 'unknown'] as const;

export const ContactFieldDefinitionSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  label: z.string(),
  labelI18n: z.record(z.string(), z.string()),
  type: z.string(),
  allowedValues: z.array(z.string()).nullable(),
  editable: z.boolean(),
  piiClassification: z.string(),
});
export type ContactFieldDefinition = z.infer<typeof ContactFieldDefinitionSchema>;

export const ContactDetailResponseSchema = z.object({
  contact: ContactSchema,
  phoneType: z.enum(PHONE_TYPES).nullable(),
  suppressions: z.array(
    z.object({
      id: UuidSchema,
      channel: z.enum(CONSENT_CHANNELS),
      reason: z.enum(SUPPRESSION_REASONS),
      createdAt: z.string(),
    }),
  ),
  reviewRequests: z.object({
    total: z.number().int(),
    latest: z
      .object({
        id: UuidSchema,
        status: z.string(),
        channel: z.string(),
        sentiment: z.string().nullable(),
        createdAt: z.string(),
        googleClickedAt: z.string().nullable(),
      })
      .nullable(),
  }),
  metrics: z
    .object({
      visitCount: z.number().int(),
      purchaseCount: z.number().int(),
      totalSpendCents: z.number().int(),
      lastVisitAt: z.string().nullable(),
      lastPurchaseAt: z.string().nullable(),
    })
    .nullable(),
  /** Active custom field definitions for contacts (empty on Basic): drives the "attributes" section. */
  fieldDefinitions: z.array(ContactFieldDefinitionSchema),
  possibleDuplicates: z.array(ContactBriefSchema),
});
export type ContactDetailResponse = z.infer<typeof ContactDetailResponseSchema>;

// ── Consent & suppression ────────────────────────────────────────────────────

export const ConsentUpdateRequestSchema = z.object({
  channel: z.enum(CONSENT_CHANNELS),
  state: z.enum(CONSENT_STATES),
  purpose: z.enum(CONSENT_PURPOSES).default('marketing'),
  method: z.enum(CONSENT_METHODS).optional(),
  note: optionalText(500),
  lawfulBasis: z.enum(LAWFUL_BASES).optional(),
  /**
   * Re-granting a channel that is on the suppression list because of a *manual/bounce/invalid*
   * entry. Opt-outs made by the person (unsubscribe link, STOP, complaint) can never be lifted
   * by staff; the person must opt in again themselves.
   */
  liftSuppression: z.boolean().default(false),
});
export type ConsentUpdateRequest = z.input<typeof ConsentUpdateRequestSchema>;

export const ConsentEventSchema = z.object({
  id: UuidSchema,
  channel: z.enum(CONSENT_CHANNELS),
  purpose: z.enum(CONSENT_PURPOSES),
  state: z.enum(CONSENT_STATES),
  lawfulBasis: z.enum(LAWFUL_BASES).nullable(),
  source: z.enum(CONSENT_EVENT_SOURCES),
  evidence: z.record(z.string(), z.unknown()),
  actorType: z.string(),
  occurredAt: z.string(),
});
export type ConsentEvent = z.infer<typeof ConsentEventSchema>;

export const ConsentHistoryResponseSchema = z.object({ items: z.array(ConsentEventSchema) });

export const SuppressionSchema = z.object({
  id: UuidSchema,
  channel: z.enum(CONSENT_CHANNELS),
  value: z.string(),
  reason: z.enum(SUPPRESSION_REASONS),
  source: z.string().nullable(),
  contactId: UuidSchema.nullable(),
  /** False for the person's own opt-outs (unsubscribe/STOP/complaint). */
  removable: z.boolean(),
  createdAt: z.string(),
});
export type Suppression = z.infer<typeof SuppressionSchema>;

export const SuppressionListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  channel: z.enum(CONSENT_CHANNELS).optional(),
  cursor: z.string().max(600).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const SuppressionListResponseSchema = z.object({
  items: z.array(SuppressionSchema),
  nextCursor: z.string().nullable(),
});

export const CreateSuppressionRequestSchema = z.object({
  channel: z.enum(CONSENT_CHANNELS),
  value: z.string().trim().min(3).max(254),
  /**
   * Staff can only add removable reasons. `unsubscribed` / `complaint` / `sms_stop` claim the
   * person opted out themselves and are permanent, so only the unsubscribe link, STOP replies and
   * provider complaints create them; a verbal request is recorded as `manual` (with a note).
   */
  reason: z.enum(['manual', 'invalid', 'bounced']).default('manual'),
});
export type CreateSuppressionRequest = z.input<typeof CreateSuppressionRequestSchema>;

// ── Bulk ─────────────────────────────────────────────────────────────────────

export const BULK_ACTIONS = ['tag', 'untag', 'archive', 'unarchive', 'delete'] as const;
export type BulkAction = (typeof BULK_ACTIONS)[number];

/** Hard cap on contacts touched by one bulk request ("all matching" included). */
export const BULK_MAX_CONTACTS = 20_000;

export const BulkActionRequestSchema = z
  .object({
    action: z.enum(BULK_ACTIONS),
    tags: TagListSchema.optional(),
    /** Either explicit ids… */
    ids: z.array(UuidSchema).min(1).max(5000).optional(),
    /** …or every contact matching a filter (minus `excludeIds`). */
    filter: ContactFilterSchema.optional(),
    excludeIds: z.array(UuidSchema).max(5000).optional(),
  })
  .refine((v) => Boolean(v.ids) !== Boolean(v.filter), {
    message: 'provide exactly one of ids or filter',
    path: ['ids'],
  })
  .refine((v) => !(v.action === 'tag' || v.action === 'untag') || (v.tags?.length ?? 0) > 0, {
    message: 'tags are required for tag/untag',
    path: ['tags'],
  });
export type BulkActionRequest = z.input<typeof BulkActionRequestSchema>;

export const BulkActionResponseSchema = z.object({ affected: z.number().int() });

// ── Duplicates & merge ───────────────────────────────────────────────────────

export const DUPLICATE_REASONS = ['phone', 'email', 'name'] as const;

export const DuplicateCandidateSchema = z.object({
  a: ContactBriefSchema,
  b: ContactBriefSchema,
  reasons: z.array(z.enum(DUPLICATE_REASONS)),
  confidence: z.enum(['high', 'medium', 'low']),
});
export type DuplicateCandidate = z.infer<typeof DuplicateCandidateSchema>;

export const DuplicateListResponseSchema = z.object({ items: z.array(DuplicateCandidateSchema) });

export const DismissDuplicateRequestSchema = z.object({
  contactId: UuidSchema,
  relatedContactId: UuidSchema,
});

export const MERGE_FIELDS = [
  'firstName',
  'lastName',
  'displayName',
  'email',
  'phone',
  'birthDate',
  'locale',
] as const;
export type MergeField = (typeof MERGE_FIELDS)[number];

export const MergeChoiceSchema = z.enum(['primary', 'merged']);

/**
 * The one consent rule for a merge, per channel (used by the API merge, its preview and the merge
 * dialog, so staff see exactly what gets saved):
 *  - a `denied` on EITHER card wins, whatever address survives (the person's opt-out is never
 *    dropped, even when it was given for the other number/email);
 *  - otherwise, no surviving address → `unknown`;
 *  - same address on both cards → `granted` beats `unknown`;
 *  - different addresses → the state that belongs to the address that survives (a consent given
 *    for the dropped address never carries over to another one).
 */
export function resolveMergedConsent(input: {
  primary: { address: string | null; state: ConsentState };
  merged: { address: string | null; state: ConsentState };
  /** The address the merged contact keeps for this channel. */
  finalAddress: string | null;
}): ConsentState {
  const { primary, merged, finalAddress } = input;
  if (primary.state === 'denied' || merged.state === 'denied') return 'denied';
  if (!finalAddress) return 'unknown';
  if (primary.address === merged.address)
    return primary.state === 'granted' || merged.state === 'granted' ? 'granted' : 'unknown';
  if (finalAddress === merged.address) return merged.state;
  if (finalAddress === primary.address) return primary.state;
  return 'unknown';
}

export const MergePreviewResponseSchema = z.object({
  primary: ContactSchema,
  merged: ContactSchema,
  fields: z.array(
    z.object({
      field: z.enum(MERGE_FIELDS),
      primary: z.string().nullable(),
      merged: z.string().nullable(),
      conflict: z.boolean(),
      suggested: MergeChoiceSchema,
    }),
  ),
  tags: z.array(z.string()),
  /**
   * Resulting consent with the suggested field choices (see `resolveMergedConsent`; the dialog
   * recomputes it when staff pick the other phone/email). A denial on either side always wins.
   */
  consent: z.object({ email: z.enum(CONSENT_STATES), sms: z.enum(CONSENT_STATES) }),
  /** Records that move to the primary contact. */
  moves: z.object({
    consentEvents: z.number().int(),
    reviewRequests: z.number().int(),
    deliveries: z.number().int(),
    customerEvents: z.number().int(),
  }),
});
export type MergePreviewResponse = z.infer<typeof MergePreviewResponseSchema>;

export const MergeRequestSchema = z
  .object({
    primaryId: UuidSchema,
    mergedId: UuidSchema,
    fieldChoices: z.partialRecord(z.enum(MERGE_FIELDS), MergeChoiceSchema).default({}),
  })
  .refine((v) => v.primaryId !== v.mergedId, {
    message: 'cannot merge a contact into itself',
    path: ['mergedId'],
  });
export type MergeRequest = z.input<typeof MergeRequestSchema>;

export const MergeResponseSchema = z.object({ contact: ContactSchema, mergeId: UuidSchema });

// ── Timeline ─────────────────────────────────────────────────────────────────

export const TIMELINE_KINDS = [
  'created',
  'consent',
  'delivery',
  'review_request',
  'review_event',
  'customer_event',
  'merge',
  'import',
  'form_response',
] as const;
export type TimelineKind = (typeof TIMELINE_KINDS)[number];

export const TimelineItemSchema = z.object({
  id: z.string(),
  kind: z.enum(TIMELINE_KINDS),
  occurredAt: z.string(),
  data: z.record(z.string(), z.unknown()),
});
export type TimelineItem = z.infer<typeof TimelineItemSchema>;

export const TimelineResponseSchema = z.object({ items: z.array(TimelineItemSchema) });

// ── Import (Basic: fixed columns; Pro adds `field:<id>` targets via the schema module) ──

/** Limits (spec §6.5 / task): 10 MB file, 20 000 data rows, 100 columns. */
export const IMPORT_MAX_BYTES = 10_000_000;
export const IMPORT_MAX_ROWS = 20_000;
export const IMPORT_MAX_COLUMNS = 100;

export const IMPORT_CORE_TARGETS = [
  'first_name',
  'last_name',
  'full_name',
  'email',
  'phone',
  'tags',
  'birthday',
  'ignore',
] as const;
export type ImportCoreTarget = (typeof IMPORT_CORE_TARGETS)[number];

/** Core target or a custom field (`field:<field definition uuid>`, Pro only). */
export const ImportTargetSchema = z.union([
  z.enum(IMPORT_CORE_TARGETS),
  z.string().regex(/^field:[0-9a-f-]{36}$/),
]);
export type ImportTarget = z.infer<typeof ImportTargetSchema>;

export const IMPORT_UPDATE_MODES = ['fill_empty', 'overwrite', 'skip'] as const;
export type ImportUpdateMode = (typeof IMPORT_UPDATE_MODES)[number];

export const ImportColumnSchema = z.object({
  index: z
    .number()
    .int()
    .min(0)
    .max(IMPORT_MAX_COLUMNS - 1),
  target: ImportTargetSchema,
});

/** Lawful bases an importer may declare (must be explicit — "unknown" is refused). */
export const IMPORT_LAWFUL_BASES = [
  'consent',
  'existing_customer',
  'legitimate_interest',
  'contract',
] as const;

export const ImportMappingRequestSchema = z
  .object({
    columns: z.array(ImportColumnSchema).min(1).max(IMPORT_MAX_COLUMNS),
    hasHeader: z.boolean().default(true),
    lawfulBasis: z.enum(IMPORT_LAWFUL_BASES),
    /** The user explicitly declares they have the right to use these contacts. */
    lawfulBasisConfirmed: z.literal(true),
    consentDefaults: z.object({
      email: z.enum(CONSENT_STATES),
      sms: z.enum(CONSENT_STATES),
    }),
    consentMethod: z.enum(CONSENT_METHODS).optional(),
    updateMode: z.enum(IMPORT_UPDATE_MODES).default('fill_empty'),
    defaultTags: TagListSchema.default([]),
    /** Off by default: imported contacts do not trigger automatic review requests. */
    autoSendReviewRequests: z.boolean().default(false),
  })
  .refine((v) => v.columns.some((c) => c.target === 'email' || c.target === 'phone'), {
    message: 'map at least one email or phone column',
    path: ['columns'],
  })
  .refine(
    (v) => {
      const singles = v.columns.filter((c) => c.target !== 'ignore' && c.target !== 'tags');
      return new Set(singles.map((c) => c.target)).size === singles.length;
    },
    { message: 'each target can be mapped once', path: ['columns'] },
  )
  .refine((v) => new Set(v.columns.map((c) => c.index)).size === v.columns.length, {
    message: 'each column can be mapped once',
    path: ['columns'],
  })
  // "granted" is recorded consent evidence: only a consent basis can back it. The existing-customer
  // exception is not consent — those contacts stay `unknown` (marketing still reaches them under
  // that basis, see messaging/eligibility.ts) so no false consent outlives a later basis change.
  .refine(
    (v) =>
      (v.consentDefaults.email !== 'granted' && v.consentDefaults.sms !== 'granted') ||
      v.lawfulBasis === 'consent',
    {
      message: 'granted consent requires the consent basis',
      path: ['consentDefaults'],
    },
  );
export type ImportMappingRequest = z.input<typeof ImportMappingRequestSchema>;

export const ImportStatsSchema = z.object({
  total: z.number().int().optional(),
  toCreate: z.number().int().optional(),
  toUpdate: z.number().int().optional(),
  duplicates: z.number().int().optional(),
  invalid: z.number().int().optional(),
  skipped: z.number().int().optional(),
  suppressed: z.number().int().optional(),
  /** Rows skipped because the person was erased (GDPR) earlier; included in `skipped`. */
  erased: z.number().int().optional(),
  created: z.number().int().optional(),
  updated: z.number().int().optional(),
  rolledBack: z.number().int().optional(),
  rollbackSkipped: z.number().int().optional(),
  valid: z.number().int().optional(),
});
export type ImportStats = z.infer<typeof ImportStatsSchema>;

export const ImportSchema = z.object({
  id: UuidSchema,
  filename: z.string(),
  status: z.enum(IMPORT_STATUSES),
  mode: z.enum(IMPORT_MODES),
  fileType: z.enum(['csv', 'xlsx']).nullable(),
  encoding: z.string().nullable(),
  delimiter: z.string().nullable(),
  hasHeader: z.boolean(),
  headers: z.array(z.string()),
  /** Physical rows in the file (header included). */
  rowCount: z.number().int(),
  mapping: z.object({ columns: z.array(ImportColumnSchema) }).nullable(),
  lawfulBasis: z.enum(LAWFUL_BASES).nullable(),
  options: z.record(z.string(), z.unknown()),
  stats: ImportStatsSchema,
  committedThroughRow: z.number().int(),
  error: z.string().nullable(),
  rollbackAvailable: z.boolean(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});
export type ImportJob = z.infer<typeof ImportSchema>;

export const ImportSuggestionSchema = z.object({
  index: z.number().int(),
  target: ImportTargetSchema,
  confidence: z.number().min(0).max(1),
  reason: z.enum(['header', 'content', 'ai']),
});
export type ImportSuggestion = z.infer<typeof ImportSuggestionSchema>;

export const ImportPreviewResponseSchema = z.object({
  import: ImportSchema,
  /** First rows as parsed (header row included when present), for the mapping preview. */
  sampleRows: z.array(z.array(z.string())),
  suggestions: z.array(ImportSuggestionSchema),
  /** True when an AI mapping proposer is registered and the plan allows it (Pro). */
  aiMappingAvailable: z.boolean(),
});
export type ImportPreviewResponse = z.infer<typeof ImportPreviewResponseSchema>;

export const ImportRowSchema = z.object({
  rowNumber: z.number().int(),
  status: z.enum(IMPORT_ROW_STATUSES),
  values: z.array(z.string()),
  errors: z.array(
    z.object({ field: z.string().optional(), code: z.string(), message: z.string() }),
  ),
  action: z.enum(['created', 'updated', 'skipped']).nullable(),
  plan: z.enum(['create', 'update', 'skip']).nullable(),
  contactId: UuidSchema.nullable(),
  /** Recorded on purpose without a contact (an anonymous retail sale), not an error. */
  anonymous: z.boolean().optional(),
});
export type ImportRow = z.infer<typeof ImportRowSchema>;

export const ImportRowsQuerySchema = z.object({
  status: z.enum([...IMPORT_ROW_STATUSES, 'problems']).optional(),
  cursor: z.coerce.number().int().min(0).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const ImportRowsResponseSchema = z.object({
  items: z.array(ImportRowSchema),
  nextCursor: z.string().nullable(),
});

export const ImportListResponseSchema = z.object({ items: z.array(ImportSchema) });

export const ImportRollbackResponseSchema = z.object({
  import: ImportSchema,
  rolledBack: z.number().int(),
  skipped: z.array(z.object({ rowNumber: z.number().int(), reason: z.string() })),
});
